import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ProgressBarStatus } from "@financeable-com-au/financeable-ui";
// @ts-expect-error: ignoring as it is existing js file
import { loanDetailsSelector } from "@/store/slices/applicationFormSlice";
import { InArrears } from "@/store/slices/types/applicationFormSlice";
import type { ApplicationFormSlice, LoanDetails } from "@/store/slices/types/applicationFormSlice";
import { useProductRequiredFields } from "@/hooks/useRequiredFields";
import { useLenderCard, useQuoteCalculatorForm, useLenderCardCustom } from "@/components/LenderProductCard/hooks";
import { LenderCardContext } from "./LenderCardContext";
import type { LenderProduct, RemainingDataFields } from "./types/LenderProductTypes";

interface LenderCardProviderProps {
  productDetails: LenderProduct | undefined;
  showRequiredFields: (fields: RemainingDataFields) => void;
  isSingleLenderProduct: boolean;
  children: React.ReactNode;
}

const LenderCardProvider = ({
  productDetails,
  showRequiredFields,
  isSingleLenderProduct,
  children,
}: LenderCardProviderProps) => {
  const loanDetails = useSelector<ApplicationFormSlice, LoanDetails>(loanDetailsSelector);
  const [showCriteria, setShowCriteria] = useState(false);
  const [showQuoteCalculator, setShowQuoteCalculator] = useState(false);
  const [showDisclosureStatement, setShowDisclosureStatement] = useState(false);
  const [inArrears, setInArrears] = useState<InArrears>(() =>
    loanDetails.lender === productDetails?.lender ? (loanDetails.inArrears ?? InArrears.Yes) : InArrears.Yes,
  );
  const requiredFields = useProductRequiredFields(productDetails);
  const lenderCard = useLenderCard({ product: productDetails, isInArrears: inArrears === InArrears.Yes });
  const { quoteCalculatorFormik, isCustomQuote } = useQuoteCalculatorForm({ product: productDetails });
  const lenderCardCustom = useLenderCardCustom({
    formik: quoteCalculatorFormik,
    inArrearsOverride: productDetails ? inArrears : undefined,
  });

  useEffect(() => {
    if (loanDetails.lender === productDetails?.lender) {
      setInArrears(loanDetails.inArrears ?? InArrears.Yes);
    }
  }, [loanDetails.inArrears, loanDetails.lender, productDetails?.lender]);

  return (
    <LenderCardContext.Provider
      value={{
        product: productDetails,
        lenderCard,
        lenderCardCustom,
        quoteCalculatorFormik,
        showCriteria,
        showQuoteCalculator,
        showDisclosureStatement,
        isSingleLenderProduct,
        inArrears,
        requiredFields: {
          remainingFields: requiredFields?.remainingFields || [],
          fieldsTotalLength: requiredFields?.fieldsTotalLength || 0,
          fieldsCheckedPercentage: requiredFields?.fieldsCheckedPercentage || 0,
          fieldsDoneLength: requiredFields?.fieldsDoneLength || 0,
          fieldsStatus: requiredFields?.fieldsStatus as ProgressBarStatus,
          isFieldsCompleted: requiredFields?.isFieldsCompleted || false,
        },
        isCustomQuote,
        setInArrears,
        setShowCriteria,
        setShowQuoteCalculator,
        setShowDisclosureStatement,
        showRequiredFields,
      }}
    >
      {children}
    </LenderCardContext.Provider>
  );
};

export { LenderCardProvider };
